import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import LinkIcon from '@mui/icons-material/Link';
import NotesIcon from '@mui/icons-material/Notes';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import memoryApi from './memoryApi';
import networkApi from '../Network/services/networkApi';
import { swalConfirmDelete } from '../../utils/swal';

const FACE_PAGE_SIZE = 24;

function csvToList(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function listToCsv(value) {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'string') return value;
  return '';
}

function uniqueList(values) {
  const seen = new Set();
  return values
    .map((value) => String(value || '').trim())
    .filter((value) => {
      const key = value.toLowerCase();
      if (!value || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function normaliseFace(face, fallbackGroupId = '') {
  const item = face && typeof face === 'object' ? face : {};
  const aliases = uniqueList([item.nickname, ...(Array.isArray(item.aliases) ? item.aliases : csvToList(item.aliases))]);
  return {
    group_id: String(item.group_id || fallbackGroupId || ''),
    display_name: String(item.display_name || item.name || ''),
    aliases,
    tags: Array.isArray(item.tags) ? item.tags : csvToList(item.tags),
    notes: String(item.notes || ''),
    thumbnail_event_id: item.thumbnail_event_id || '',
    thumbnail_group: item.thumbnail_group || item.group_id || fallbackGroupId || '',
    linked_customer_id: item.linked_customer_id || '',
    linked_customer_name: String(item.linked_customer_name || ''),
    linked_username: String(item.linked_username || ''),
    is_self: Boolean(item.is_self),
    photo_count: Number(item.photo_count || 0),
    face_count: Number(item.face_count || item.photo_count || 0),
    event_count: Number(item.event_count || 0),
    embedding_models: Array.isArray(item.embedding_models) ? item.embedding_models : [],
    embedding_model_count: Number(item.embedding_model_count || 0),
    thumbnail_key: String(item.thumbnail_key || ''),
  };
}

function normaliseFaces(value) {
  if (Array.isArray(value)) return value.map((face, index) => normaliseFace(face, face?.group_id || index)).filter((face) => face.group_id);
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value)
    .map(([groupId, face]) => normaliseFace(face, groupId))
    .filter((face) => face.group_id);
}

function connectionLabel(conn) {
  if (!conn) return '';
  const name = conn.customer_name || conn.linked_customer_name || 'Connected user';
  return conn.username ? `${name} (@${conn.username})` : name;
}

function faceTitle(face) {
  return face.display_name || (face.aliases || [])[0] || `Face group ${face.group_id}`;
}

function isAssigned(face) {
  return Boolean(String(face.display_name || '').trim() || (face.aliases || []).length);
}

function draftFromFace(face) {
  return {
    display_name: face.display_name || '',
    aliases: listToCsv(face.aliases),
    tags: listToCsv(face.tags),
    notes: face.notes || '',
    linked_customer_id: face.linked_customer_id ? String(face.linked_customer_id) : '',
    linked_customer_name: face.linked_customer_name || '',
    linked_username: face.linked_username || '',
    is_self: Boolean(face.is_self),
    thumbnail_event_id: face.thumbnail_event_id || '',
    thumbnail_group: face.thumbnail_group || face.group_id || '',
  };
}

function payloadFromDraft(draft) {
  return {
    display_name: draft.display_name,
    nickname: '',
    aliases: csvToList(draft.aliases),
    tags: csvToList(draft.tags),
    notes: draft.notes,
    linked_customer_id: draft.linked_customer_id || null,
    linked_customer_name: draft.linked_customer_name || '',
    linked_username: draft.linked_username || '',
    is_self: Boolean(draft.is_self),
    thumbnail_event_id: draft.thumbnail_event_id || '',
    thumbnail_group: draft.thumbnail_group || '',
  };
}

function serialiseDraft(draft) {
  return JSON.stringify(payloadFromDraft(draft || {}));
}

function FaceThumbnail({ thumbnailUrl }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [thumbnailUrl]);
  const url = !failed ? thumbnailUrl : '';
  return (
    <Box sx={{ width: 52, height: 52, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover', display: 'grid', placeItems: 'center' }}>
      {url ? (
        <Box component="img" src={url} alt="" onError={() => setFailed(true)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <FaceRetouchingNaturalIcon color="disabled" />
      )}
    </Box>
  );
}

function NotesTagsDialog({ open, title, tags, notes, onClose, onSave }) {
  const [tagsDraft, setTagsDraft] = useState(tags || '');
  const [notesDraft, setNotesDraft] = useState(notes || '');

  useEffect(() => {
    setTagsDraft(tags || '');
    setNotesDraft(notes || '');
  }, [tags, notes, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <TextField
            label="Tags"
            value={tagsDraft}
            onChange={(event) => setTagsDraft(event.target.value)}
            helperText="Comma separated context for search"
            multiline
            minRows={2}
            fullWidth
          />
          <TextField
            label="Notes"
            value={notesDraft}
            onChange={(event) => setNotesDraft(event.target.value)}
            multiline
            minRows={4}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(tagsDraft, notesDraft)} sx={{ textTransform: 'none' }}>Apply</Button>
      </DialogActions>
    </Dialog>
  );
}

function ConnectionSearchDialog({ open, currentConnection, isSelf, onClose, onSelect, onSelectSelf }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setLoading(true);
    networkApi.getConnections()
      .then((data) => setResults(Array.isArray(data) ? data : []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(() => {
      const clean = query.trim();
      setLoading(true);
      const request = clean ? networkApi.searchConnections(clean) : networkApi.getConnections();
      request
        .then((data) => setResults(Array.isArray(data) ? data : []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [open, query]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link network connection</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          {isSelf && (
            <Alert severity="info">
              This face is tagged as myself. Saving will clear the myself tag from any other face.
            </Alert>
          )}
          {currentConnection?.customer_id && !isSelf && (
            <Alert severity="info">
              Currently linked to {connectionLabel(currentConnection)}.
            </Alert>
          )}
          <TextField
            label="Search accepted connections"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoFocus
            fullWidth
            size="small"
          />
          <Paper variant="outlined" sx={{ maxHeight: 320, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            ) : results.length ? (
              <List disablePadding>
                {results.map((conn) => (
                  <ListItemButton key={conn.customer_id} onClick={() => onSelect(conn)}>
                    <ListItemText
                      primary={conn.customer_name || 'Unnamed user'}
                      secondary={conn.username ? `@${conn.username}` : 'Accepted connection'}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">No accepted connections found.</Typography>
              </Box>
            )}
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1} sx={{ mr: 'auto' }}>
          <Button onClick={onSelectSelf} sx={{ textTransform: 'none' }}>
            Tag as myself
          </Button>
          {(currentConnection?.customer_id || isSelf) && (
            <Button color="error" onClick={() => onSelect(null)} sx={{ textTransform: 'none' }}>
              Unlink
            </Button>
          )}
        </Stack>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

const FaceMemoryRow = React.memo(function FaceMemoryRow({ face, draft, thumbnailUrl, onDraftChange, onDelete, onRefreshThumbnail, savingDisabled }) {
  const [dialog, setDialog] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [refreshingThumbnail, setRefreshingThumbnail] = useState(false);

  const updateDraft = (patch) => onDraftChange(face.group_id, patch);

  const currentConnection = draft.linked_customer_id && !draft.is_self
    ? {
      customer_id: draft.linked_customer_id,
      customer_name: draft.linked_customer_name,
      username: draft.linked_username,
    }
    : null;

  const remove = async () => {
    setDeleting(true);
    try {
      await onDelete(face.group_id, faceTitle({ ...face, ...draft, aliases: csvToList(draft.aliases) }));
    } finally {
      setDeleting(false);
    }
  };

  const refreshThumbnail = async () => {
    setRefreshingThumbnail(true);
    try {
      await onRefreshThumbnail(face);
    } finally {
      setRefreshingThumbnail(false);
    }
  };
  const faceCount = Number(face.face_count || face.photo_count || 0);
  const photoCount = Number(face.photo_count || 0);

  return (
    <TableRow hover>
      <TableCell sx={{ width: 74 }}><FaceThumbnail thumbnailUrl={thumbnailUrl} /></TableCell>
      <TableCell sx={{ minWidth: 110 }}>
        <Typography variant="body2" fontWeight={700}>{face.group_id}</Typography>
        <Chip
          size="small"
          label={isAssigned({ ...face, ...draft, aliases: csvToList(draft.aliases) }) ? 'Named' : 'Unassigned'}
          color={isAssigned({ ...face, ...draft, aliases: csvToList(draft.aliases) }) ? 'primary' : 'default'}
          variant="outlined"
          sx={{ mt: 0.5 }}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 96 }}>
        <Typography variant="body2" fontWeight={700}>{faceCount}</Typography>
        <Typography variant="caption" color="text.secondary">
          {photoCount} photo{photoCount === 1 ? '' : 's'} / {(face.event_count || 0)} event{Number(face.event_count || 0) === 1 ? '' : 's'}
        </Typography>
        <Box sx={{ mt: 0.75 }}>
          <Tooltip title={(face.embedding_models || []).length ? `Embeddings: ${(face.embedding_models || []).join(', ')}` : 'No saved face embeddings'}>
            <Chip
              size="small"
              variant="outlined"
              label={`${face.embedding_model_count || 0} model${Number(face.embedding_model_count || 0) === 1 ? '' : 's'}`}
            />
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell sx={{ minWidth: 190 }}>
        <TextField
          label="Name"
          size="small"
          value={draft.display_name}
          onChange={(event) => updateDraft({ display_name: event.target.value })}
          fullWidth
        />
      </TableCell>
      <TableCell sx={{ minWidth: 260 }}>
        <TextField
          label="Aliases"
          size="small"
          value={draft.aliases}
          onChange={(event) => updateDraft({ aliases: event.target.value })}
          placeholder="Nicknames and aliases, comma separated"
          fullWidth
        />
      </TableCell>
      <TableCell sx={{ minWidth: 220 }}>
        <Stack spacing={0.5}>
          <Typography variant="body2" noWrap>
            {draft.is_self ? 'Myself' : currentConnection ? connectionLabel(currentConnection) : 'Not connected'}
          </Typography>
          <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={() => setDialog('connection')} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
            Link
          </Button>
        </Stack>
        <ConnectionSearchDialog
          open={dialog === 'connection'}
          currentConnection={currentConnection}
          isSelf={draft.is_self}
          onClose={() => setDialog('')}
          onSelect={(conn) => {
            updateDraft({
              linked_customer_id: conn?.customer_id ? String(conn.customer_id) : '',
              linked_customer_name: conn?.customer_name || '',
              linked_username: conn?.username || '',
              is_self: false,
            });
            setDialog('');
          }}
          onSelectSelf={() => {
            updateDraft({
              linked_customer_id: '',
              linked_customer_name: '',
              linked_username: '',
              is_self: true,
            });
            setDialog('');
          }}
        />
      </TableCell>
      <TableCell align="right" sx={{ width: 220 }}>
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
          <Tooltip title="Refresh thumbnail from tagged photos">
            <span>
              <IconButton size="small" onClick={refreshThumbnail} disabled={savingDisabled || refreshingThumbnail}>
                {refreshingThumbnail ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Edit notes & tags">
            <IconButton size="small" onClick={() => setDialog('notesTags')}>
              <NotesIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete face memory">
            <span>
              <IconButton size="small" color="error" onClick={remove} disabled={savingDisabled || deleting}>
                {deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <NotesTagsDialog
          open={dialog === 'notesTags'}
          title={`Notes & tags for ${faceTitle(face)}`}
          tags={draft.tags}
          notes={draft.notes}
          onClose={() => setDialog('')}
          onSave={(tagsValue, notesValue) => {
            updateDraft({ tags: tagsValue, notes: notesValue });
            setDialog('');
          }}
        />
      </TableCell>
    </TableRow>
  );
});

const FaceMemoryCard = React.memo(function FaceMemoryCard({ face, draft, thumbnailUrl, onDraftChange, onDelete, onRefreshThumbnail, savingDisabled }) {
  const [dialog, setDialog] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [refreshingThumbnail, setRefreshingThumbnail] = useState(false);

  const updateDraft = (patch) => onDraftChange(face.group_id, patch);

  const currentConnection = draft.linked_customer_id && !draft.is_self
    ? {
      customer_id: draft.linked_customer_id,
      customer_name: draft.linked_customer_name,
      username: draft.linked_username,
    }
    : null;

  const remove = async () => {
    setDeleting(true);
    try {
      await onDelete(face.group_id, faceTitle({ ...face, ...draft, aliases: csvToList(draft.aliases) }));
    } finally {
      setDeleting(false);
    }
  };

  const refreshThumbnail = async () => {
    setRefreshingThumbnail(true);
    try {
      await onRefreshThumbnail(face);
    } finally {
      setRefreshingThumbnail(false);
    }
  };
  const faceCount = Number(face.face_count || face.photo_count || 0);
  const photoCount = Number(face.photo_count || 0);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FaceThumbnail thumbnailUrl={thumbnailUrl} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700}>{face.group_id}</Typography>
            <Chip
              size="small"
              label={isAssigned({ ...face, ...draft, aliases: csvToList(draft.aliases) }) ? 'Named' : 'Unassigned'}
              color={isAssigned({ ...face, ...draft, aliases: csvToList(draft.aliases) }) ? 'primary' : 'default'}
              variant="outlined"
              sx={{ mt: 0.5 }}
            />
          </Box>
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Refresh thumbnail from tagged photos">
              <span>
                <IconButton size="small" onClick={refreshThumbnail} disabled={savingDisabled || refreshingThumbnail}>
                  {refreshingThumbnail ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete face memory">
              <span>
                <IconButton size="small" color="error" onClick={remove} disabled={savingDisabled || deleting}>
                  {deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
          <Typography variant="body2" fontWeight={700}>{faceCount}</Typography>
          <Typography variant="caption" color="text.secondary">
            {photoCount} photo{photoCount === 1 ? '' : 's'} / {(face.event_count || 0)} event{Number(face.event_count || 0) === 1 ? '' : 's'}
          </Typography>
          <Tooltip title={(face.embedding_models || []).length ? `Embeddings: ${(face.embedding_models || []).join(', ')}` : 'No saved face embeddings'}>
            <Chip
              size="small"
              variant="outlined"
              label={`${face.embedding_model_count || 0} model${Number(face.embedding_model_count || 0) === 1 ? '' : 's'}`}
            />
          </Tooltip>
        </Stack>

        <TextField
          label="Name"
          size="small"
          value={draft.display_name}
          onChange={(event) => updateDraft({ display_name: event.target.value })}
          fullWidth
        />
        <TextField
          label="Aliases"
          size="small"
          value={draft.aliases}
          onChange={(event) => updateDraft({ aliases: event.target.value })}
          placeholder="Nicknames and aliases, comma separated"
          fullWidth
        />

        <Stack spacing={0.5}>
          <Typography variant="body2" noWrap>
            {draft.is_self ? 'Myself' : currentConnection ? connectionLabel(currentConnection) : 'Not connected'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={() => setDialog('connection')} sx={{ textTransform: 'none' }}>
              Link
            </Button>
            <Button size="small" variant="outlined" startIcon={<EditNoteIcon />} onClick={() => setDialog('notesTags')} sx={{ textTransform: 'none' }}>
              Notes/Tags
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <ConnectionSearchDialog
        open={dialog === 'connection'}
        currentConnection={currentConnection}
        isSelf={draft.is_self}
        onClose={() => setDialog('')}
        onSelect={(conn) => {
          updateDraft({
            linked_customer_id: conn?.customer_id ? String(conn.customer_id) : '',
            linked_customer_name: conn?.customer_name || '',
            linked_username: conn?.username || '',
            is_self: false,
          });
          setDialog('');
        }}
        onSelectSelf={() => {
          updateDraft({
            linked_customer_id: '',
            linked_customer_name: '',
            linked_username: '',
            is_self: true,
          });
          setDialog('');
        }}
      />
      <NotesTagsDialog
        open={dialog === 'notesTags'}
        title={`Notes & tags for ${faceTitle(face)}`}
        tags={draft.tags}
        notes={draft.notes}
        onClose={() => setDialog('')}
        onSave={(tagsValue, notesValue) => {
          updateDraft({ tags: tagsValue, notes: notesValue });
          setDialog('');
        }}
      />
    </Paper>
  );
});

export default function Memory() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [savingNotes, setSavingNotes] = useState(false);
  const [deletingAllFaces, setDeletingAllFaces] = useState(false);
  const [rebuildingFaceStats, setRebuildingFaceStats] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [faces, setFaces] = useState([]);
  const [faceDrafts, setFaceDrafts] = useState({});
  const [savingFaces, setSavingFaces] = useState(false);
  const [faceSaveStatus, setFaceSaveStatus] = useState('idle');
  const [savedFaceSerials, setSavedFaceSerials] = useState({});
  const [totalFaces, setTotalFaces] = useState(0);
  const [filteredFaceCount, setFilteredFaceCount] = useState(0);
  const [generalNotes, setGeneralNotes] = useState('');
  const [facePage, setFacePage] = useState(1);
  const [filterText, setFilterText] = useState('');
  const [connectionFilter, setConnectionFilter] = useState('all');
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const lastSavedFacesRef = useRef({});
  const faceDraftsRef = useRef({});
  const thumbnailUrlCacheRef = useRef({});

  const facePageCount = Math.max(1, Math.ceil(filteredFaceCount / FACE_PAGE_SIZE));
  const dirtyFaceIds = useMemo(() => (
    Object.entries(faceDrafts)
      .filter(([groupId, draft]) => serialiseDraft(draft) !== savedFaceSerials[groupId])
      .map(([groupId]) => groupId)
  ), [faceDrafts, savedFaceSerials]);

  useEffect(() => {
    faceDraftsRef.current = faceDrafts;
  }, [faceDrafts]);

  const loadMemoryPage = useCallback(async (options = {}) => {
    const page = options.page || facePage;
    const q = options.q ?? filterText;
    const connectionStatus = options.connectionStatus || connectionFilter;
    setLoading(true);
    setError('');
    try {
      const data = await memoryApi.getMemory({
        limit: FACE_PAGE_SIZE,
        offset: (page - 1) * FACE_PAGE_SIZE,
        q,
        connection_status: connectionStatus,
      });
      const loadedFaces = normaliseFaces(data.face_list || data.faces);
      setFaces(loadedFaces);
      setFaceDrafts((prev) => {
        const next = { ...prev };
        const saved = { ...lastSavedFacesRef.current };
        for (const face of loadedFaces) {
          const draft = draftFromFace(face);
          const serialised = serialiseDraft(draft);
          if (!next[face.group_id] || serialiseDraft(next[face.group_id]) === saved[face.group_id]) {
            next[face.group_id] = draft;
          }
          saved[face.group_id] = serialised;
        }
        lastSavedFacesRef.current = saved;
        setSavedFaceSerials(saved);
        return next;
      });
      setTotalFaces(Number(data.face_count || 0));
      setFilteredFaceCount(Number(data.filtered_face_count ?? data.face_count ?? 0));
      setGeneralNotes(data.general_notes || '');
    } catch (err) {
      setError(err.message || 'Failed to load Memory.');
    } finally {
      setLoading(false);
    }
  }, [connectionFilter, facePage, filterText]);

  useEffect(() => {
    const handle = setTimeout(() => {
      loadMemoryPage();
    }, 250);
    return () => clearTimeout(handle);
  }, [loadMemoryPage]);

  useEffect(() => {
    setFacePage(1);
  }, [filterText, connectionFilter]);

  useEffect(() => {
    const now = Date.now();
    const visibleKeys = uniqueList(faces.map((face) => face.thumbnail_key));
    if (!visibleKeys.length) {
      setThumbnailUrls({});
      return undefined;
    }

    const cachedUrls = {};
    const missing = [];
    for (const key of visibleKeys) {
      const cached = thumbnailUrlCacheRef.current[key];
      if (cached?.url && Number(cached.expiresAt || 0) - now > 60_000) {
        cachedUrls[key] = cached.url;
      } else {
        missing.push(key);
      }
    }
    setThumbnailUrls(cachedUrls);
    if (!missing.length) return undefined;

    let active = true;
    memoryApi.getFaceThumbnailUrls(missing)
      .then((payload) => {
        if (!active) return;
        const urls = payload?.urls && typeof payload.urls === 'object' ? payload.urls : {};
        const expiresIn = Number(payload?.expires_in || 3600);
        const expiresAt = Date.now() + Math.max(60, expiresIn - 60) * 1000;
        for (const [key, url] of Object.entries(urls)) {
          if (url) thumbnailUrlCacheRef.current[key] = { url, expiresAt };
        }
        setThumbnailUrls((prev) => {
          const next = { ...prev };
          for (const key of visibleKeys) {
            const cached = thumbnailUrlCacheRef.current[key];
            if (cached?.url && Number(cached.expiresAt || 0) - Date.now() > 0) {
              next[key] = cached.url;
            }
          }
          return next;
        });
      })
      .catch(() => {});

    return () => { active = false; };
  }, [faces]);

  const updateFaceDraft = useCallback((groupId, patch) => {
    setFaceDrafts((prev) => ({
      ...prev,
      [groupId]: {
        ...(prev[groupId] || draftFromFace({ group_id: groupId })),
        ...patch,
      },
    }));
  }, []);

  const saveDirtyFaces = useCallback(async () => {
    const dirty = Object.entries(faceDraftsRef.current).filter(([groupId, draft]) => (
      serialiseDraft(draft) !== lastSavedFacesRef.current[groupId]
    ));
    if (!dirty.length) return;
    setSavingFaces(true);
    setFaceSaveStatus('saving');
    setError('');
    try {
      const facesPayload = dirty.map(([groupId, draft]) => ({
        group_id: groupId,
        ...payloadFromDraft(draft),
      }));
      await memoryApi.updateFaces(facesPayload);
      dirty.forEach(([groupId, draft]) => {
        lastSavedFacesRef.current[groupId] = serialiseDraft(draft);
      });
      setSavedFaceSerials({ ...lastSavedFacesRef.current });
      setFaceSaveStatus('saved');
      setNotice(`Saved ${dirty.length} face ${dirty.length === 1 ? 'row' : 'rows'}.`);
      await loadMemoryPage();
    } catch (err) {
      setFaceSaveStatus('idle');
      setError(err.message || 'Failed to save face memory.');
    } finally {
      setSavingFaces(false);
    }
  }, [loadMemoryPage]);

  useEffect(() => {
    if (faceSaveStatus !== 'saved') return undefined;
    const timer = setTimeout(() => setFaceSaveStatus('idle'), 2500);
    return () => clearTimeout(timer);
  }, [faceSaveStatus]);

  const deleteFace = useCallback(async (groupId, title) => {
    const ok = await swalConfirmDelete({
      title: `Delete ${title || `face group ${groupId}`}?`,
      text: 'This removes this face from account Memory and future auto-naming seeds. Event galleries are not changed.',
      confirmText: 'Delete face',
    });
    if (!ok) return;
    setError('');
    try {
      await memoryApi.deleteFace(groupId);
      setFaceDrafts((prev) => {
        const next = { ...prev };
        delete next[groupId];
        return next;
      });
      delete lastSavedFacesRef.current[groupId];
      setSavedFaceSerials({ ...lastSavedFacesRef.current });
      setNotice('Face memory deleted.');
      await loadMemoryPage();
    } catch (err) {
      setError(err.message || 'Failed to delete face memory.');
    }
  }, [loadMemoryPage]);

  const refreshFaceThumbnail = useCallback(async (face) => {
    setError('');
    try {
      const result = await memoryApi.refreshFaceThumbnail(face.group_id);
      const key = result?.thumbnail_key || face.thumbnail_key;
      if (key) {
        delete thumbnailUrlCacheRef.current[key];
      } else {
        thumbnailUrlCacheRef.current = {};
      }
      setNotice(`Thumbnail refreshed for ${faceTitle(face)}.`);
      await loadMemoryPage();
    } catch (err) {
      setError(err.message || 'Failed to refresh face thumbnail.');
    }
  }, [loadMemoryPage]);

  const deleteAllFaces = async () => {
    const ok = await swalConfirmDelete({
      title: 'Delete all face memory?',
      text: 'This clears all account-level face names and face embedding seeds. Search memory notes are kept, and existing event galleries are not changed.',
      confirmText: 'Delete all faces',
    });
    if (!ok) return;
    setDeletingAllFaces(true);
    setError('');
    try {
      await memoryApi.deleteAllFaces();
      setFaces([]);
      setTotalFaces(0);
      setFilteredFaceCount(0);
      setFacePage(1);
      lastSavedFacesRef.current = {};
      setFaceDrafts({});
      setSavedFaceSerials({});
      setNotice('All face memory deleted.');
    } catch (err) {
      setError(err.message || 'Failed to delete face memory.');
    } finally {
      setDeletingAllFaces(false);
    }
  };

  const rebuildFaceStats = async () => {
    setRebuildingFaceStats(true);
    setError('');
    try {
      await memoryApi.rebuildFaceStats();
      setNotice('Face stats reloaded.');
      await loadMemoryPage();
    } catch (err) {
      setError(err.message || 'Failed to reload face stats.');
    } finally {
      setRebuildingFaceStats(false);
    }
  };

  const saveGeneralNotes = async () => {
    setSavingNotes(true);
    setError('');
    try {
      await memoryApi.updateGeneralNotes(generalNotes);
      setNotice('Search memory notes saved.');
    } catch (err) {
      setError(err.message || 'Failed to save notes.');
    } finally {
      setSavingNotes(false);
    }
  };

  const rangeStart = filteredFaceCount ? (facePage - 1) * FACE_PAGE_SIZE + 1 : 0;
  const rangeEnd = Math.min(facePage * FACE_PAGE_SIZE, filteredFaceCount);

  return (
    <Container maxWidth="xl" sx={{ py: 4, pb: { xs: 10, md: 4 } }}>
      <Stack spacing={3}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <FaceRetouchingNaturalIcon color="primary" sx={{ fontSize: { xs: 26, md: 34 } }} />
            <Box>
              <Typography variant="h4" fontWeight={900} sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>Memory</Typography>
              <Typography color="text.secondary" sx={{ fontSize: { xs: '0.85rem', md: undefined } }}>
                {isMobile ? 'Manage face names and search memory.' : 'Manage private face names, network associations, and personal search context across your events.'}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <NotesIcon color="primary" />
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1.05rem', md: undefined } }}>Search memory notes</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: undefined } }}>
                  {isMobile ? 'Add private context for AI search.' : 'Add private terminology, venue context, translations, abbreviations, or company-specific event language.'}
                </Typography>
              </Box>
            </Stack>
            <TextField
              value={generalNotes}
              onChange={(event) => setGeneralNotes(event.target.value)}
              multiline
              minRows={5}
              placeholder={'Example: In our family, "ko" means cow. Company events happen in auditoriums X, Y, and Z.'}
              fullWidth
            />
            <Box>
              <Button
                variant="contained"
                startIcon={savingNotes ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                disabled={savingNotes}
                onClick={saveGeneralNotes}
                sx={{ textTransform: 'none' }}
              >
                Save search memory
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Divider />

        <Stack spacing={1}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1.05rem', md: undefined } }}>Recognised faces</Typography>
              <Typography color="text.secondary" variant="body2" sx={{ fontSize: { xs: '0.8rem', md: undefined } }}>
                {isMobile ? 'Named groups appear first.' : 'Named groups are listed first. Link a face to an accepted network connection when the person also uses CircleMoments.'}
              </Typography>
            </Box>
            <Chip icon={<SearchIcon />} label={`${totalFaces} global groups`} color="primary" variant="outlined" />
          </Stack>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} alignItems="center" flexWrap="wrap">
              <Chip
                label={
                  faceSaveStatus === 'saving'
                    ? 'Saving...'
                    : faceSaveStatus === 'saved'
                      ? 'All saved'
                      : dirtyFaceIds.length
                        ? `${dirtyFaceIds.length} unsaved ${dirtyFaceIds.length === 1 ? 'row' : 'rows'}`
                        : 'No unsaved changes'
                }
                color={faceSaveStatus === 'saved' ? 'success' : dirtyFaceIds.length ? 'warning' : 'default'}
                variant="outlined"
              />
              <Button
                variant="contained"
                size="small"
                startIcon={savingFaces ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                disabled={savingFaces || dirtyFaceIds.length === 0}
                onClick={() => saveDirtyFaces()}
                sx={{ textTransform: 'none' }}
              >
                {savingFaces ? 'Saving...' : `Save all${dirtyFaceIds.length ? ` (${dirtyFaceIds.length})` : ''}`}
              </Button>
          </Stack>
          </Stack>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              label="Filter names, aliases, tags, notes, username"
              size="small"
              value={filterText}
              onChange={(event) => setFilterText(event.target.value)}
              fullWidth
            />
            <FormControl size="small" sx={{ minWidth: 230 }}>
              <InputLabel>Network status</InputLabel>
              <Select
                label="Network status"
                value={connectionFilter}
                onChange={(event) => setConnectionFilter(event.target.value)}
              >
                <MenuItem value="all">All faces</MenuItem>
                <MenuItem value="connected">Connected in network</MenuItem>
                <MenuItem value="not_connected">Not connected in network</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : totalFaces ? (
          <Stack spacing={2}>
            {isMobile ? (
              <Stack spacing={1.5}>
                {faces.map((face) => (
                  <FaceMemoryCard
                    key={face.group_id}
                    face={face}
                    draft={faceDrafts[face.group_id] || draftFromFace(face)}
                    thumbnailUrl={thumbnailUrls[face.thumbnail_key] || ''}
                    onDraftChange={updateFaceDraft}
                    onDelete={deleteFace}
                    onRefreshThumbnail={refreshFaceThumbnail}
                    savingDisabled={savingFaces}
                  />
                ))}
              </Stack>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Face</TableCell>
                      <TableCell>Group</TableCell>
                      <TableCell>Stats</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Aliases</TableCell>
                      <TableCell>Network</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {faces.map((face) => (
                      <FaceMemoryRow
                        key={face.group_id}
                        face={face}
                        draft={faceDrafts[face.group_id] || draftFromFace(face)}
                        thumbnailUrl={thumbnailUrls[face.thumbnail_key] || ''}
                        onDraftChange={updateFaceDraft}
                        onDelete={deleteFace}
                        onRefreshThumbnail={refreshFaceThumbnail}
                        savingDisabled={savingFaces}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {filteredFaceCount === 0 && (
              <Alert severity="info">No face memory rows match the current filters.</Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                Showing {rangeStart}-{rangeEnd} of {filteredFaceCount}
              </Typography>
              {filteredFaceCount > FACE_PAGE_SIZE && (
                <Pagination
                  count={facePageCount}
                  page={Math.min(facePage, facePageCount)}
                  onChange={(_, page) => setFacePage(page)}
                  color="primary"
                  siblingCount={1}
                  boundaryCount={1}
                />
              )}
            </Stack>
            {totalFaces > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  color="primary"
                  variant="outlined"
                  size="small"
                  startIcon={rebuildingFaceStats ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                  disabled={rebuildingFaceStats || deletingAllFaces}
                  onClick={rebuildFaceStats}
                  sx={{ textTransform: 'none' }}
                >
                  Reload face stats
                </Button>
                <Button
                  color="error"
                  variant="outlined"
                  size="small"
                  startIcon={deletingAllFaces ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                  disabled={deletingAllFaces || rebuildingFaceStats}
                  onClick={deleteAllFaces}
                  sx={{ textTransform: 'none' }}
                >
                  Delete all faces
                </Button>
              </Box>
            )}
          </Stack>
        ) : (
          <Alert severity="info">
            No account-level face memory yet. Run face grouping on an event and name a group to seed Memory.
          </Alert>
        )}
      </Stack>
    </Container>
  );
}
